import {
  POLITICA_PRIVACIDADE_VERSAO,
  TITULAR_TIPOS,
  TITULAR_TIPO_LABEL,
  type TitularTipo,
} from '@econdominio/core/privacidade';
import { useCallback, useEffect, useState } from 'react';
import { getJson, mutateJson } from '../api/client';
import { Alert, Badge, Button, Card, Spinner } from '../components/ui';
import { PoliticaConteudo } from './PoliticaConteudo';

interface ConsentResp {
  versaoVigente: string;
  consentido: boolean;
}
interface Solicitacao {
  id: number;
  tipo: TitularTipo;
  detalhe: string | null;
  status: string;
  criado_em: string;
}

function formatarData(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString('pt-BR');
}

/** Tela autenticada (#74): política + consentimento + solicitações do titular. */
export function Privacidade() {
  const [consent, setConsent] = useState<ConsentResp | null>(null);
  const [solics, setSolics] = useState<Solicitacao[] | null>(null);
  const [tipo, setTipo] = useState<TitularTipo>('acesso');
  const [detalhe, setDetalhe] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);

  const load = useCallback(async () => {
    const [c, s] = await Promise.all([
      getJson<ConsentResp>('/api/titular/consentimento'),
      getJson<{ solicitacoes: Solicitacao[] }>('/api/titular/solicitacao'),
    ]);
    setConsent(c);
    setSolics(s?.solicitacoes ?? []);
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function aceitar() {
    setErro(null);
    const res = await mutateJson('/api/titular/consentimento', {
      versao: POLITICA_PRIVACIDADE_VERSAO,
      aceito: true,
    });
    if (!res.ok) {
      setErro('Não foi possível registrar o consentimento.');
      return;
    }
    setOk('Consentimento registrado.');
    void load();
  }

  async function enviarSolicitacao() {
    setErro(null);
    setOk(null);
    setEnviando(true);
    const res = await mutateJson('/api/titular/solicitacao', { tipo, detalhe: detalhe.trim() });
    setEnviando(false);
    if (!res.ok) {
      setErro('Não foi possível enviar a solicitação.');
      return;
    }
    setDetalhe('');
    setOk('Solicitação registrada. A administração dará andamento.');
    void load();
  }

  return (
    <div style={{ display: 'grid', gap: 'var(--space-4)' }}>
      {erro ? <Alert kind="danger">{erro}</Alert> : null}
      {ok ? <Alert kind="success">{ok}</Alert> : null}

      <Card>
        <h2 style={{ marginTop: 0 }}>Consentimento</h2>
        {consent === null ? (
          <Spinner />
        ) : consent.consentido ? (
          <Badge>Consentido — versão {consent.versaoVigente}</Badge>
        ) : (
          <div style={{ display: 'grid', gap: 'var(--space-2)' }}>
            <Alert kind="info">
              Você ainda não registrou o aceite da versão vigente da política.
            </Alert>
            <div>
              <Button size="sm" onClick={aceitar}>
                Li e concordo com a Política de Privacidade
              </Button>
            </div>
          </div>
        )}
      </Card>

      <Card>
        <h2 style={{ marginTop: 0 }}>Solicitar sobre meus dados</h2>
        <div style={{ display: 'grid', gap: 'var(--space-3)', maxWidth: 460 }}>
          <label style={{ display: 'grid', gap: '0.35rem' }}>
            <span style={{ fontSize: '0.85rem' }}>Tipo de solicitação</span>
            <select
              value={tipo}
              onChange={(e) => setTipo(e.target.value as TitularTipo)}
              style={{ padding: '0.4rem 0.5rem' }}
            >
              {TITULAR_TIPOS.map((t) => (
                <option key={t} value={t}>
                  {TITULAR_TIPO_LABEL[t]}
                </option>
              ))}
            </select>
          </label>
          <label style={{ display: 'grid', gap: '0.35rem' }}>
            <span style={{ fontSize: '0.85rem' }}>Detalhe (opcional)</span>
            <textarea
              value={detalhe}
              onChange={(e) => setDetalhe(e.target.value)}
              rows={3}
              maxLength={2000}
              style={{ padding: '0.4rem 0.5rem' }}
            />
          </label>
          <div>
            <Button size="sm" onClick={enviarSolicitacao} disabled={enviando}>
              {enviando ? <Spinner /> : 'Enviar solicitação'}
            </Button>
          </div>
        </div>

        <h3>Minhas solicitações</h3>
        {solics === null ? (
          <Spinner />
        ) : solics.length === 0 ? (
          <p style={{ opacity: 0.7 }}>Nenhuma solicitação registrada.</p>
        ) : (
          <ul style={{ display: 'grid', gap: '0.5rem', listStyle: 'none', padding: 0 }}>
            {solics.map((s) => (
              <li
                key={s.id}
                style={{
                  display: 'flex',
                  gap: '0.5rem',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <span>
                  {TITULAR_TIPO_LABEL[s.tipo]} · {formatarData(s.criado_em)}
                </span>
                <Badge>{s.status}</Badge>
              </li>
            ))}
          </ul>
        )}
      </Card>

      <Card>
        <h2 style={{ marginTop: 0 }}>Política de Privacidade</h2>
        <PoliticaConteudo />
      </Card>
    </div>
  );
}
